import React, { useState, useMemo } from 'react';
import AnnotatedText from './AnnotatedText';
import DetailedPatternAnalysis from './DetailedPatternAnalysis';
import './markup.css';

const EssayMarkupView = ({ essayText, markupData }) => {
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);

  // Error boundary wrapper
  try {
  
  // Parse the markup data if it's valid
  const { annotations = [], patternAnalysis, overallScore, overallFeedback } = markupData || {};
  
  // Create the marked up text with character-precise annotations
  const renderMarkedUpText = useMemo(() => {
    if (!essayText || !annotations || annotations.length === 0) {
      return <div className="unmarked-text">{essayText}</div>;
    }
    
    let result = [];
    let lastIndex = 0;
    
    // Sort annotations by start index to prevent overlapping issues
    const sortedAnnotations = [...annotations].sort((a, b) => a.startIndex - b.startIndex);
    
    // Handle overlapping annotations by merging or adjusting
    const processedAnnotations = [];
    sortedAnnotations.forEach((annotation) => {
      if (processedAnnotations.length === 0) {
        processedAnnotations.push(annotation);
      } else {
        const lastAnnotation = processedAnnotations[processedAnnotations.length - 1];
        // If current annotation starts before last one ends, adjust or skip
        if (annotation.startIndex < lastAnnotation.endIndex) {
          // Skip overlapping annotations for now (could merge in future)
          console.warn('Skipping overlapping annotation:', annotation);
        } else {
          processedAnnotations.push(annotation);
        }
      }
    });
    
    processedAnnotations.forEach((annotation, i) => {
      // Add text before this annotation
      if (annotation.startIndex > lastIndex) {
        result.push(
          <span key={`text-${i}`} className="unmarked-text">
            {essayText.slice(lastIndex, annotation.startIndex)}
          </span>
        );
      }
      
      // Add the annotated text
      const annotatedText = essayText.slice(annotation.startIndex, annotation.endIndex);
      result.push(
        <AnnotatedText
          key={`annotation-${annotation.id}`}
          annotation={annotation}
          text={annotatedText}
          onSelect={() => setSelectedAnnotation(annotation)}
          isSelected={selectedAnnotation?.id === annotation.id}
        />
      );
      
      lastIndex = annotation.endIndex;
    });
    
    // Add remaining text after last annotation
    if (lastIndex < essayText.length) {
      result.push(
        <span key="text-final" className="unmarked-text">
          {essayText.slice(lastIndex)}
        </span>
      );
    }
    
    return result;
  }, [essayText, annotations, selectedAnnotation]);
  
  return (
    <>
      <div className="essay-paper-simplified">
        {/* Essay Header with Grade */}
        {overallScore !== undefined && (
          <div className="essay-header-simplified">
            <div className="grade-circle">
              {overallScore}
            </div>
          </div>
        )}
        
        {/* Overall Feedback Section */}
        {overallFeedback && (
          <div style={{
            background: 'linear-gradient(135deg, #f5f3ff 0%, #f0ebff 100%)',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            marginTop: '1rem',
            borderLeft: '4px solid #7c3aed'
          }}>
            <h3 style={{
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#5b21b6',
              marginBottom: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
            }}>Overall Assessment</h3>
            <p style={{
              fontSize: '0.95rem',
              lineHeight: '1.6',
              color: '#4c1d95',
              fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
            }}>{overallFeedback}</p>
          </div>
        )}
        
        {/* Essay Content with Markup */}
        <div className="essay-content">
          {renderMarkedUpText}
        </div>
      </div>
      
      {/* Selected Annotation Detail - Floating */}
      {selectedAnnotation && (
        <div className="floating-feedback-detail">
          <div className="feedback-detail-content">
            <button 
              onClick={() => setSelectedAnnotation(null)}
              className="close-button"
            >
              ×
            </button>
            
            <div className="feedback-category">
              <span className={`category-badge ${selectedAnnotation.category}`}>
                {selectedAnnotation.category.toUpperCase()}
              </span>
              <span className={`severity-badge ${selectedAnnotation.severity}`}>
                {selectedAnnotation.severity.toUpperCase()}
              </span>
            </div>
            
            <div className="selected-text">
              <h4>SELECTED TEXT:</h4>
              <p>"{selectedAnnotation.originalText}"</p>
            </div>
            
            <div className="teacher-feedback">
              <h4>FEEDBACK:</h4>
              <p>{selectedAnnotation.feedback}</p>
            </div>
            
            {selectedAnnotation.suggestedText && (
              <div className="suggested-revision">
                <h4>SUGGESTED REVISION:</h4>
                <p>"{selectedAnnotation.suggestedText}"</p>
              </div>
            )}
            
            {selectedAnnotation.explanation && (
              <div className="explanation">
                <h4>WHY THIS HELPS:</h4>
                <p>{selectedAnnotation.explanation}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Detailed Pattern Analysis */}
      {(patternAnalysis || annotations.length > 0) && (
        <div className="mt-8">
          <DetailedPatternAnalysis 
            patternAnalysis={patternAnalysis}
            annotations={annotations}
            essayText={essayText}
          />
        </div>
      )}
    </>
  );

  } catch (error) {
    console.error('Error rendering EssayMarkupView:', error);
    
    // Fallback UI
    return (
      <div className="essay-paper-simplified">
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 m-4">
          <div className="flex items-start gap-2">
            <div className="text-red-600">⚠️</div>
            <div>
              <h3 className="font-semibold text-red-800 mb-2">Unable to Display Essay Markup</h3>
              <p className="text-sm text-red-700 mb-3">
                There was an error rendering the marked-up essay. The original text is displayed below.
              </p>
              <div className="bg-white p-4 rounded border border-red-200 mt-3">
                <pre className="whitespace-pre-wrap text-sm text-slate-800 font-serif leading-relaxed">
                  {essayText || 'No essay text available'}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default EssayMarkupView;